/**
 * Séance de révision reliée à la base : chaque note est enregistrée (journal + état FSRS)
 * avant que la séance avance ; « annuler » annule aussi la révision dans le journal.
 */
import { creerPlanificateur, type Note } from '../fsrs/planificateur';
import type { Aleatoire } from '../seance/aleatoire';
import { debutJournee } from '../seance/journee';
import {
  annulerDerniereReponse,
  carteCourante,
  demarrerSeance,
  noter,
  retirerCartes,
  type ApresNote,
  type Seance,
} from '../seance/seance';
import type { CarteLue } from './conversions';
import type { Depot } from './depot';

export class ServiceSeance {
  private constructor(
    private readonly depot: Depot,
    private etat: Seance,
    /** Cartes du paquet au début de la séance (contenu à afficher, fil d'Ariane…). */
    readonly cartes: ReadonlyMap<string, CarteLue>,
    /** Identifiants des révisions enregistrées, dans l'ordre des réponses. */
    private readonly revisions: number[] = [],
  ) {}

  /** Début de séance du paquet principal : la liste des cartes est figée maintenant. */
  static async demarrer(
    depot: Depot,
    paquetId: string,
    maintenant = new Date(),
    aleatoire: Aleatoire = Math.random,
  ): Promise<ServiceSeance> {
    const reglages = await depot.reglages(paquetId);
    const [cartes, revisions] = await Promise.all([
      depot.cartesDuPaquet(paquetId),
      depot.revisionsDuPaquet(paquetId, debutJournee(maintenant, reglages.heureDebutJournee)),
    ]);
    const seance = demarrerSeance({
      cartes,
      revisions,
      reglages,
      planificateur: creerPlanificateur(reglages),
      maintenant,
      aleatoire,
    });
    return new ServiceSeance(depot, seance, new Map(cartes.map((carte) => [carte.id, carte])));
  }

  get seance(): Seance {
    return this.etat;
  }

  /** Carte courante, avec son état FSRS à jour. */
  get carteCourante(): CarteLue | null {
    const carte = carteCourante(this.etat);
    return carte === null ? null : { ...this.cartes.get(carte.id)!, fsrs: carte.fsrs };
  }

  /** Durée et couleur des quatre boutons pour la carte courante. */
  apercu(maintenant = new Date()) {
    const carte = carteCourante(this.etat);
    if (carte === null) return null;
    return this.etat.contexte.planificateur.apercu(carte.id, carte.fsrs, maintenant);
  }

  /** Note la carte courante ; la séance n'avance qu'une fois la révision enregistrée. */
  async noter(note: Note, tempsReponseMs: number, maintenant = new Date()): Promise<ApresNote> {
    const resultat = noter(this.etat, note, maintenant);
    const id = await this.depot.enregistrerRevision({ ...resultat.revision, tempsReponseMs });
    this.revisions.push(id);
    this.etat = resultat.seance;
    return resultat;
  }

  /** Annule la dernière réponse. Renvoie la carte représentée, ou `null`. */
  async annuler(maintenant = new Date()): Promise<string | null> {
    const resultat = annulerDerniereReponse(this.etat, maintenant);
    const id = this.revisions.at(-1);
    if (resultat === null || id === undefined) return null;
    await this.depot.annulerRevision(id);
    this.revisions.pop();
    this.etat = resultat.seance;
    return resultat.carteId;
  }

  /**
   * Suspend la carte courante (au verso, avant de la noter) et passe à la suivante.
   * `alerteSeuil` : voir ApresNote.
   */
  async suspendreCarteCourante(maintenant = new Date()): Promise<{ alerteSeuil: boolean }> {
    const carte = carteCourante(this.etat);
    if (carte === null) return { alerteSeuil: false };
    await this.depot.suspendre(carte.id, true);
    const resultat = retirerCartes(this.etat, [carte.id], maintenant);
    this.etat = resultat.seance;
    return { alerteSeuil: resultat.alerteSeuil };
  }

  /** Après une modification qui a supprimé des cartes (trou supprimé, inverse décochée…). */
  retirerCartesSupprimees(
    carteIds: readonly string[],
    maintenant = new Date(),
  ): { alerteSeuil: boolean } {
    const resultat = retirerCartes(this.etat, carteIds, maintenant);
    this.etat = resultat.seance;
    return { alerteSeuil: resultat.alerteSeuil };
  }
}
